/**
 * ステータスを取得する。
 * @param data データ
 * @param success 成否
 * @param messages メッセージ
 * @returns ステータス
 */
export const getStatus = (
  data: any,
  success: boolean,
  messages?: Array<string> | string
): StatusProps => {
  return {
    data,
    success,
    messages:
      messages === null || messages === undefined
        ? []
        : typeof messages === 'string'
        ? [messages]
        : messages,
  }
}

/**
 * 成功ステータスを取得する。
 * @param data データ
 * @param messages メッセージ
 * @returns 成功ステータス
 */
export const success = (
  data?: any,
  messages?: Array<string> | string
): StatusProps => {
  return getStatus(data, true, messages)
}

/**
 * 失敗ステータスを取得する。
 * @param data データ
 * @param messages メッセージ
 * @returns 失敗ステータス
 */
export const fail = (
  data?: any,
  messages?: Array<string> | string
): StatusProps => {
  return getStatus(data, false, messages)
}

/**
 * 成功しているか検証する。
 * @param status ステータス
 * @returns 成功していればtrueを返却
 */
export const isSuccess = (status: StatusProps): boolean => {
  return status.success
}

/**
 * 失敗しているか検証する。
 * @param status ステータス
 * @returns 失敗していればtrueを返却
 */
export const isFail = (status: StatusProps): boolean => {
  return !status.success
}

/**
 * 複数のステータスを結合する。
 * 全てが成功している場合のみ、成功と判定される。
 * @param statuses ステータスの列挙
 * @returns 結合されたステータス
 */
export const mergeStatus = (...statuses: Array<StatusProps>): StatusProps => {
  return statuses.reduce((accumulator, current) => {
    return getStatus(
      [...accumulator.data, current.data],
      isSuccess(accumulator) && isSuccess(current),
      [...accumulator.messages, ...current.messages]
    )
  }, success([]))
}

/**
 * 型定義：ステータス
 */
export type StatusProps = {
  // 処理結果 or エラー内容のデータを格納する
  data: any
  // 処理結果が成功なら`true`, 失敗なら`false`を格納する
  success: boolean
  // ステータス関連のメッセージを格納する
  messages: Array<string>
}

/**
 * チェック対象StatusからHTTPステータスコードを取得する
 * @param status チェック対象のステータス
 * @returns ステータスコード（見つからない場合は undefined）
 */
export const getStatusCode = (status: StatusProps): number => {
  const statusData = Array.isArray(status.data) ? status.data : [status.data]
  const dataWithStatusCode = statusData
    .slice()
    .reverse()
    .find(
      (currentData: any) =>
        currentData.status ||
        (currentData.response && currentData.response.status)
    )
  return dataWithStatusCode?.status
    ? dataWithStatusCode.status
    : dataWithStatusCode?.response?.status
}
